import { useEffect, useState } from "react";
import { Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { fetchSources, adminCreateSource, adminUpdateSource, adminDeleteSource } from "@/lib/api";

const BLANK = { title: "", category: "", note: "", ref: "", url: "", order: 9999 };

export default function AdminSources() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);

  const load = () => fetchSources().then(setList).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      if (editing) { await adminUpdateSource(editing, form); toast.success("Source updated"); }
      else { await adminCreateSource(form); toast.success("Source added"); }
      setForm(BLANK); setEditing(null); load();
    } catch { toast.error("Save failed — are you logged in as admin?"); }
  };
  const edit = (s) => { setEditing(s.sid); setForm({ title: s.title || "", category: s.category || "", note: s.note || "", ref: s.ref || "", url: s.url || "", order: s.order ?? 9999 }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (sid) => { if (!window.confirm("Delete this source?")) return; await adminDeleteSource(sid); load(); };

  const F = (k, ph) => <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} data-testid={`src-${k}`} className="w-full rounded-lg border px-3 py-2 text-sm" />;

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="admin-sources">
      <h1 className="mb-1 text-2xl font-semibold">Sources &amp; References</h1>
      <p className="mb-6 text-sm text-gray-500">{list.length} sources. Shown on the public <b>/sources</b> page and the homepage trust strip.</p>

      <div className="mb-8 space-y-3 rounded-xl border bg-white p-4">
        <h2 className="font-medium">{editing ? "Edit source" : "Add a source"}</h2>
        {F("title", "Title e.g. Rig Veda")}
        <div className="grid gap-3 sm:grid-cols-2">{F("category", "Category e.g. Veda")}{F("ref", "Used in e.g. Mantras, suktas")}</div>
        {F("note", "Short description")}
        <div className="grid gap-3 sm:grid-cols-2">
          {F("url", "URL (optional)")}
          <input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} placeholder="Order" className="w-full rounded-lg border px-3 py-2 text-sm" />
        </div>
        <div className="flex gap-2">
          <button onClick={save} data-testid="src-save" className="flex items-center gap-1.5 rounded-lg bg-[var(--saffron,#c1440e)] px-4 py-2 text-sm font-medium text-white"><Save className="h-4 w-4" />{editing ? "Update" : "Add source"}</button>
          {editing && <button onClick={() => { setForm(BLANK); setEditing(null); }} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>}
        </div>
      </div>

      <div className="space-y-2">
        {list.map((s) => (
          <div key={s.sid} data-testid={`admin-source-${s.sid}`} className="flex items-start justify-between gap-3 rounded-lg border bg-white p-3">
            <div className="min-w-0">
              <p className="font-medium">{s.title} <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-500">{s.category}</span></p>
              {s.note && <p className="text-sm text-gray-500">{s.note}</p>}
            </div>
            <div className="flex shrink-0 gap-1">
              <button onClick={() => edit(s)} className="rounded border px-2 py-1 text-xs">Edit</button>
              <button onClick={() => del(s.sid)} data-testid={`del-${s.sid}`} className="rounded border border-red-200 px-2 py-1 text-xs text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
